/** The part of an antd table column this module reads (grouped columns nest under `children`). */
interface ColumnWidth {
  width?: string | number
  children?: readonly ColumnWidth[]
}

/** Narrowest a column without a `width` may get before the table scrolls sideways instead. */
const FLEX_COLUMN_MIN_WIDTH = 160

function leafColumns(columns: readonly ColumnWidth[]): ColumnWidth[] {
  return columns.flatMap((column) =>
    column.children && column.children.length > 0 ? leafColumns(column.children) : [column],
  )
}

/**
 * The `scroll.x` of a table, checked against its columns instead of only typed in by hand.
 *
 * A table with `scroll={{ x: N }}` is N pixels wide whenever its container is narrower than N, and
 * the columns that have no `width` share what the fixed-width columns leave over. With a
 * hand-written N that did not follow the columns, the left-over could be next to nothing: the
 * user-history table had x 900 and 850px of fixed columns, so its 변경 내용 column was 50px on a
 * phone and each row 200–300px tall (개발항목 199).
 *
 * This returns `atLeast` (the number the screen had) unless the width-less columns would get less
 * than `flexColumnMinWidth` each; then it returns the fixed widths plus that minimum per width-less
 * column. A table whose columns all have a `width`, or whose width-less columns already had room,
 * is exactly as before — it never returns less than `atLeast`.
 *
 * @param flexColumnMinWidth pass a larger value when the width-less column holds sentences
 */
export function scrollXForColumns(
  columns: readonly ColumnWidth[],
  atLeast: number,
  flexColumnMinWidth: number = FLEX_COLUMN_MIN_WIDTH,
): number {
  let fixedWidth = 0
  let flexColumns = 0
  for (const column of leafColumns(columns)) {
    if (typeof column.width === 'number') fixedWidth += column.width
    else flexColumns += 1
  }
  // All widths given: antd already fits such a table to `atLeast` by shrinking columns in
  // proportion, and the PC layout relies on that — leave it alone.
  if (flexColumns === 0) return atLeast
  return Math.max(atLeast, fixedWidth + flexColumns * flexColumnMinWidth)
}
